/*
  "Pairs well with" bundle: tick the products to include (this product plus any of the
  complementary ones) and add them all to the cart in one request. Each row keeps its own
  Add button, so single products can still be added on their own.
*/
if (!customElements.get('m-comp-bundle')) {
  class MCompBundle extends HTMLElement {
    connectedCallback() {
      if (this.dataset.enabled === 'false') return;
      this.button = this.querySelector('[data-comp-add]');
      if (!this.button) return;
      this.labelEl = this.querySelector('[data-comp-label]');
      this.countEl = this.querySelector('[data-comp-count]');
      this.errorEl = this.querySelector('[data-comp-error]');
      this.totalEl = this.querySelector('[data-comp-total]');
      const mainRow = this.querySelector('[data-comp-main]');
      this.mainPrice = mainRow ? Number(mainRow.dataset.priceCents) || 0 : 0;
      this.defaultLabel = this.labelEl ? this.labelEl.textContent.trim() : 'Add bundle to cart';

      this.addEventListener('change', this.onChange);
      this.button.addEventListener('click', () => this.addBundle());
      this.watchMainVariant();
      this.watchMainQuantity();
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      if (this.variantUnsubscriber) this.variantUnsubscriber();
      if (this.quantityInput && this.onQuantityInput) {
        this.quantityInput.removeEventListener('change', this.onQuantityInput);
        this.quantityInput.removeEventListener('input', this.onQuantityInput);
      }
      clearTimeout(this.resetTimer);
    }

    onChange = (event) => {
      if (event.target.closest('[data-bundle-pick], [data-upsell-variant], [data-upsell-qty]')) this.update();
    };

    get rows() {
      return Array.from(this.querySelectorAll('[data-upsell-item]'));
    }

    pickedRows() {
      return this.rows.filter((row) => {
        const box = row.querySelector('[data-bundle-pick]');
        return box && box.checked && !box.disabled;
      });
    }

    // The main product's variant is read from the product form, so whatever size or colour
    // is selected at that moment is what goes in the cart.
    mainSelection() {
      const scope = this.closest('product-info') || document;
      const input = scope.querySelector('.m-product-form--main form [name="id"], product-form form [name="id"]');
      if (!input || !input.value) return null;
      const form = input.form || input.closest('form');
      const quantityInput = form && form.querySelector('[name="quantity"]');
      const quantity = Math.max(1, parseInt(quantityInput && quantityInput.value, 10) || 1);
      return { id: Number(input.value), quantity };
    }

    // Price of one row in cents: the selected variant where there is a picker, the main
    // product's live variant price for the first row, otherwise the row's own price.
    rowPrice(row) {
      if (row.hasAttribute('data-comp-main')) return this.mainPrice;
      const select = row.querySelector('[data-upsell-variant]');
      const option = select && select.options[select.selectedIndex];
      if (option && option.dataset.priceAmount) return Number(option.dataset.priceAmount) || 0;
      return Number(row.dataset.priceCents) || 0;
    }

    total() {
      return this.pickedRows().reduce((sum, row) => {
        const quantity = row.hasAttribute('data-comp-main') ? this.mainQuantity() : this.rowQuantity(row);
        return sum + this.rowPrice(row) * quantity;
      }, 0);
    }

    rowQuantity(row) {
      const input = row.querySelector('[data-upsell-qty]');
      return Math.min(99, Math.max(1, parseInt(input && input.value, 10) || 1));
    }

    mainQuantity() {
      const main = this.mainSelection();
      return main ? main.quantity : 1;
    }

    items() {
      const list = [];
      this.pickedRows().forEach((row) => {
        if (row.hasAttribute('data-comp-main')) {
          const main = this.mainSelection();
          if (main && main.id) list.push(main);
          return;
        }
        const button = row.querySelector('[data-upsell-add]');
        const id = button && Number(button.dataset.variantId);
        if (id && !button.disabled) list.push({ id, quantity: this.rowQuantity(row) });
      });
      return list;
    }

    update() {
      const count = this.items().length;
      if (this.countEl) this.countEl.textContent = count ? `(${count})` : '';
      if (this.totalEl) this.totalEl.textContent = count ? this.money(this.total()) : '';
      this.button.disabled = count === 0;
    }

    money(cents) {
      const format =
        (window.DeliSettings && (DeliSettings.money_format || DeliSettings.moneyFormat)) || '';
      if (typeof window.formatMoney === 'function' && format) return formatMoney(cents, format);
      return (cents / 100).toFixed(2);
    }

    // The main product's quantity box lives outside this element.
    watchMainQuantity() {
      const scope = this.closest('product-info') || document;
      const input = scope.querySelector('.m-product-form--main form [name="quantity"], product-form form [name="quantity"]');
      if (!input) return;
      this.onQuantityInput = () => this.update();
      input.addEventListener('change', this.onQuantityInput);
      input.addEventListener('input', this.onQuantityInput);
      this.quantityInput = input;
    }

    watchMainVariant() {
      const mainRow = this.querySelector('[data-comp-main]');
      if (!mainRow || !(window.DeliEvents && window.DeliTheme && DeliTheme.pubSubEvents)) return;

      this.variantUnsubscriber = DeliEvents.subscribe(DeliTheme.pubSubEvents.variantChange, (event) => {
        const data = event && event.data;
        if (!data) return;
        if (this.dataset.sectionId && data.sectionId && data.sectionId !== this.dataset.sectionId) return;

        const variant = data.variant;
        const priceEl = mainRow.querySelector('[data-upsell-price]');
        const box = mainRow.querySelector('[data-bundle-pick]');
        const format =
          (window.DeliSettings && (DeliSettings.money_format || DeliSettings.moneyFormat)) || '';
        if (variant && typeof variant.price === 'number') this.mainPrice = variant.price;
        if (priceEl && variant && typeof window.formatMoney === 'function' && format) {
          priceEl.textContent = formatMoney(variant.price, format);
        }
        if (box) {
          box.disabled = !variant || variant.available === false;
          if (box.disabled) box.checked = false;
        }
        this.update();
      });
    }

    async addBundle() {
      const items = this.items();
      if (!items.length) return;

      this.button.disabled = true;
      this.button.classList.add('is-loading');
      if (this.errorEl) this.errorEl.hidden = true;

      try {
        const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
        const response = await fetch(`${root.endsWith('/') ? root : `${root}/`}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.description || data.message || 'Could not add these products to the cart');
        }

        if (this.labelEl) this.labelEl.textContent = 'Added ✓';
        if (this.countEl) this.countEl.textContent = '';
        if (this.totalEl) this.totalEl.textContent = '';
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));

        clearTimeout(this.resetTimer);
        this.resetTimer = setTimeout(() => {
          if (this.labelEl) this.labelEl.textContent = this.defaultLabel;
          this.update();
        }, 2000);
      } catch (error) {
        console.error(error);
        if (this.errorEl) {
          this.errorEl.textContent = error.message;
          this.errorEl.hidden = false;
        }
        this.button.disabled = false;
      } finally {
        this.button.classList.remove('is-loading');
      }
    }
  }

  customElements.define('m-comp-bundle', MCompBundle);
}
